import React from 'react';
import { Trash2, CheckCircle, Play, XCircle } from 'lucide-react';
import clsx from 'clsx'; 
import StarRating from './StarRating';
import getRankColor from '../../utils/getRankColor';
import { useLanguage } from '../../hooks/useLanguage';

const AnimeCard = ({ anime, onDelete, onClick, rank, status }) => {
  const { t, language } = useLanguage();

  // 年・季節の表示フォーマット関数
  const formatYearSeason = (year, season) => {
    if (language === 'en') {
      const seasonMap = {
        '春': 'Spring',
        '夏': 'Summer',
        '秋': 'Fall',
        '冬': 'Winter'
      };
      const englishSeason = seasonMap[season] || season;
      return season ? `${year} ${englishSeason}` : `${year}`;
    } else {
      return season ? `${year}年 ${season}クール` : `${year}年`;
    }
  };
  // 視聴済みエピソード数を計算（評価モード対応）
  const getWatchedCount = () => {
    if (anime.dropped) {
      return anime.droppedAtEpisode || 0;
    }
    
    return anime.episodes.filter(ep => {
      if (anime.ratingMode === 'detailed') {
        return ep.detailedRating && (
          ep.detailedRating.content > 0 || 
          ep.detailedRating.animation > 0 || 
          ep.detailedRating.sound > 0
        );
      }
      return ep.rating > 0;
    }).length;
  };

  // 平均評価を計算（評価モード対応）
  const getAverageRating = () => {
    if (anime.ratingMode === 'detailed') {
      const ratedEpisodes = anime.episodes.filter(ep => 
        ep.detailedRating && (
          ep.detailedRating.content > 0 || 
          ep.detailedRating.animation > 0 || 
          ep.detailedRating.sound > 0
        )
      );
      
      if (ratedEpisodes.length === 0) return 0;
      
      const totalAvg = ratedEpisodes.reduce((sum, ep) => {
        const ratings = [
          ep.detailedRating.content,
          ep.detailedRating.animation,
          ep.detailedRating.sound
        ].filter(r => r > 0);
        
        const epAvg = ratings.length > 0 
          ? ratings.reduce((a, b) => a + b) / ratings.length 
          : 0;
        return sum + epAvg;
      }, 0) / ratedEpisodes.length;
      
      return totalAvg;
    } else {
      const ratedEpisodes = anime.episodes.filter(ep => ep.rating > 0);
      if (ratedEpisodes.length === 0) return 0;
      return ratedEpisodes.reduce((sum, ep) => sum + ep.rating, 0) / ratedEpisodes.length;
    }
  };

  const watchedCount = getWatchedCount();
  const totalEpisodes = anime.episodes.length;
  const averageRating = getAverageRating();
  const progress = totalEpisodes > 0 ? (watchedCount / totalEpisodes) * 100 : 0;

  // ステータスアイコンの取得
  const getStatusIcon = () => {
    switch (status) {
      case 'complete':
        return <CheckCircle className="text-white" size={20} />;
      case 'watching':
        return <Play className="text-white" size={20} />;
      case 'dropped':
        return <XCircle className="text-white" size={20} />;
      default:
        return null;
    }
  };

  // ステータスカラーの取得
  const getStatusColor = () => {
    switch (status) {
      case 'complete':
        return 'bg-green-500';
      case 'watching':
        return 'bg-blue-500';
      case 'dropped':
        return 'bg-red-500';
      default:
        return '';
    }
  };

  // プログレスバーの色を取得
  const getProgressColor = () => {
    if (anime.dropped) return 'bg-red-400';
    if (progress === 100) return 'bg-green-400';
    return 'bg-gradient-to-r from-blue-500 to-purple-500';
  };

  const hasThumb = Boolean(anime.thumbnail);  // ★ 追加
    return (
    <div
      className={clsx(
        'bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer border border-gray-100 dark:border-gray-700 overflow-hidden',
        'relative',
        hasThumb ? 'md:pb-0 pb-10' : 'pb-4'   // ★ サムネ無しは余白縮小
      )}
    >
      {/* ステータスバッジ */}
      {status && status !== 'not-started' && (
        <div className={`absolute top-3 right-3 z-10 ${getStatusColor()} p-2 rounded-full shadow-lg`}>
          {getStatusIcon()}
        </div>
      )}

      {anime.thumbnail && (
        <div className="h-48 w-full overflow-hidden" onClick={onClick}>
          <img
            src={anime.thumbnail}
            alt={anime.title}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        </div>
      )}

      <div className="p-6" onClick={onClick}>
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h3 className="font-bold text-lg text-gray-800 dark:text-white">
                {anime.title}
                {anime.subtitle && (
                  <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">{anime.subtitle}</span>
                )}
              </h3>
              {rank && (
                <span className={`px-2 py-1 text-xs font-bold rounded ${getRankColor(rank)}`}>
                  {rank}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {formatYearSeason(anime.year, anime.season)} • {totalEpisodes}{t('episodeSuffix')}
            </p>
            {anime.dropped && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                {t('droppedAt', { number: anime.droppedAtEpisode })}
              </p>
            )}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(t('deleteConfirm'))) {
                onDelete(anime.id);
              }
            }}
            className={clsx(
              'text-red-400 hover:text-red-600 p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all',
              hasThumb
                ? 'md:static absolute bottom-3 right-3 z-20'           // サムネ有り → 右下固定
                : status && status!=='not-started'
                    ? 'absolute top-14 right-3 z-20'                    // サムネ無し & ステータス有 → バッジの下
                    : 'absolute top-3 right-3 z-20'                     // サムネ無し & バッジ無し → 右上
            )}
          >
            <Trash2 size={18} />
          </button>
        </div>

        <div className="flex items-center justify-between mb-4">
          {averageRating > 0 ? (
            <div className="flex items-center gap-2">
              <StarRating rating={averageRating} size={16} allowHalf={true} />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{averageRating.toFixed(1)}</span>
              {anime.ratingMode === 'detailed' && (
                <span className="text-xs text-gray-500 dark:text-gray-400">({t('detailed')})</span>
              )}
            </div>
          ) : (
            <span className="text-sm text-gray-400 dark:text-gray-500">{t('noRating')}</span>
          )}

          <span className="text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
            {watchedCount}/{totalEpisodes}
          </span>
        </div>

        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2">
          <div
            className={`h-3 rounded-full transition-all duration-500 ${getProgressColor()}`}
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          {anime.dropped ? t('viewingAbandoned') : t('progress', { progress: progress.toFixed(0) })}
        </p>
      </div>
    </div>
  );
};

export default AnimeCard;